import { useCallback, useEffect, useState } from "react";

import { useNavigate, useParams } from "react-router-dom";

import { useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft,
  Building2,
  Clock,
  Mail,
  MapPin,
  MessageSquare,
  Package,
  Phone,
  Save,
  ShoppingCart,
  User,
} from "lucide-react";

import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "~/components/ui/card";
import { Skeleton } from "~/components/ui/skeleton";
import { Textarea } from "~/components/ui/textarea";
import { toast } from "~/hooks/use-toast";
import { supabase } from "~/integrations/supabase/client";
import { Database } from "~/integrations/supabase/types";

import { CotizacionSourceBadge } from "./CotizacionSourceBadge";

type ContactAttempt = Database["public"]["Tables"]["contact_attempts"]["Row"];

interface CartItem {
  id?: string;
  name: string;
  quantity: number;
  price?: number;
  description?: string;
}

const CotizacionDetailPage = () => {
  const { cotizacionId } = useParams<{ cotizacionId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [cotizacion, setCotizacion] = useState<ContactAttempt | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Form state
  const [notes, setNotes] = useState("");

  const fetchCotizacion = useCallback(async () => {
    if (!cotizacionId) return;

    try {
      const { data, error } = await supabase
        .from("contact_attempts")
        .select("*")
        .eq("id", cotizacionId)
        .single();

      if (error) {
        throw error;
      }

      setCotizacion(data);
      setNotes(data.notes || "");

      // Mark as read if not already read
      if (data.is_read === false || data.is_read === null) {
        const { error: updateError } = await supabase
          .from("contact_attempts")
          .update({ is_read: true })
          .eq("id", cotizacionId);

        if (updateError) {
          console.error("Error marking cotizacion as read:", updateError);
        } else {
          // Update local state immediately
          setCotizacion((prev) => (prev ? { ...prev, is_read: true } : null));
          // Invalidate unread count cache to update sidebar
          queryClient.invalidateQueries({
            queryKey: ["contact-attempts", "unread-count"],
          });
        }
      }
    } catch (error) {
      console.error("Error fetching cotizacion:", error);
      toast({
        title: "Error",
        description: "No se pudo cargar la cotización",
        variant: "destructive",
      });
      navigate("/cotizaciones");
    }
  }, [cotizacionId, navigate, queryClient]);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await fetchCotizacion();
      setLoading(false);
    };

    loadData();
  }, [cotizacionId, fetchCotizacion]);

  const handleSave = async () => {
    if (!cotizacion) return;

    try {
      setSaving(true);

      const { error } = await supabase
        .from("contact_attempts")
        .update({
          notes: notes || null,
          updated_at: new Date().toISOString(),
        })
        .eq("id", cotizacion.id);

      if (error) {
        throw error;
      }

      toast({
        title: "Éxito",
        description: "Cotización actualizada correctamente",
      });

      // Refresh the data
      await fetchCotizacion();
    } catch (error) {
      console.error("Error updating cotizacion:", error);
      toast({
        title: "Error",
        description: "No se pudo actualizar la cotización",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleWhatsApp = () => {
    if (!cotizacion) return;

    const message = `Hola ${cotizacion.full_name}, recibimos tu ${cotizacion.is_quotation ? "solicitud de cotización" : "consulta"} a través de nuestro sitio web. Nos pondremos en contacto contigo para ayudarte.`;
    const phone = cotizacion.phone.replace(/[^\d]/g, ""); // Remove non-numeric characters
    const url = `https://wa.me/${phone}?text=${encodeURIComponent(message)}`;
    window.open(url, "_blank");
  };

  const handleEmail = () => {
    if (!cotizacion) return;

    const subject = `Re: ${cotizacion.is_quotation ? "Solicitud de Cotización" : "Consulta"} - ${cotizacion.company}`;
    const body = `Estimado/a ${cotizacion.full_name},

Gracias por contactarnos a través de nuestro sitio web.

Hemos recibido tu ${cotizacion.is_quotation ? "solicitud de cotización" : "consulta"} y nos pondremos en contacto contigo a la brevedad para ayudarte con tus requerimientos.

Mensaje original:
"${cotizacion.message}"

Saludos cordiales,
Equipo Aliace`;

    const url = `mailto:${cotizacion.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.open(url, "_blank");
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("es-CL", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const parseCartItems = (cartItems: any): CartItem[] => {
    if (!cartItems) return [];

    try {
      if (Array.isArray(cartItems)) {
        return cartItems;
      }

      if (
        typeof cartItems === "object" &&
        cartItems.items &&
        Array.isArray(cartItems.items)
      ) {
        return cartItems.items;
      }

      if (typeof cartItems === "string") {
        const parsed = JSON.parse(cartItems);
        return Array.isArray(parsed) ? parsed : parsed.items || [];
      }
    } catch (error) {
      console.error("Error parsing cart items:", error);
    }

    return [];
  };

  if (loading) {
    return (
      <div className="w-[75vw] space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <Skeleton className="h-9 w-24" />
            <div className="space-y-2">
              <Skeleton className="h-9 w-64" />
              <Skeleton className="h-4 w-48" />
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <Skeleton className="h-9 w-28" />
            <Skeleton className="h-9 w-24" />
          </div>
        </div>

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          {/* Main Content */}
          <div className="space-y-6 lg:col-span-2">
            {Array.from({ length: 3 }).map((_, i) => (
              <Card key={i}>
                <CardHeader>
                  <Skeleton className="h-5 w-48" />
                </CardHeader>
                <CardContent className="space-y-3">
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-4 w-5/6" />
                  <Skeleton className="h-4 w-2/3" />
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {Array.from({ length: 2 }).map((_, i) => (
              <Card key={i}>
                <CardHeader>
                  <Skeleton className="h-5 w-32" />
                </CardHeader>
                <CardContent className="space-y-3">
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-9 w-full" />
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (!cotizacion) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="text-center">
          <h2 className="text-xl font-semibold">Cotización no encontrada</h2>
          <Button onClick={() => navigate("/cotizaciones")} className="mt-4">
            Volver a la lista
          </Button>
        </div>
      </div>
    );
  }

  const cartItems = parseCartItems(cotizacion.cart_items);
  const cardFade =
    "duration-300 animate-in fade-in-0 slide-in-from-top-1 fill-mode-both";

  return (
    <div className="w-[75vw] space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate("/cotizaciones")}
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Volver
          </Button>
          <div>
            <div className="flex items-center space-x-2">
              <h1 className="text-3xl font-bold">{cotizacion.full_name}</h1>
              {cotizacion.is_quotation && (
                <Badge className="border-orange-200 bg-orange-100 text-orange-800">
                  <ShoppingCart className="mr-1 h-3 w-3" />
                  Cotización
                </Badge>
              )}
              <CotizacionSourceBadge source={cotizacion.source} />
            </div>
            <p className="text-muted-foreground">
              Solicitud recibida el {formatDate(cotizacion.created_at)}
            </p>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          <Button
            variant="outline"
            onClick={handleWhatsApp}
            className="border-green-200 bg-green-50 text-green-700 hover:bg-green-100"
          >
            <MessageSquare className="mr-2 h-4 w-4" />
            WhatsApp
          </Button>
          <Button variant="outline" onClick={handleEmail}>
            <Mail className="mr-2 h-4 w-4" />
            Email
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Main Content */}
        <div className="space-y-6 lg:col-span-2">
          {/* Contact Information */}
          <Card className={cardFade} style={{ animationDelay: "0ms" }}>
            <CardHeader>
              <CardTitle className="flex items-center">
                <User className="mr-2 h-5 w-5" />
                Información del Cliente
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <Mail className="h-4 w-4 text-muted-foreground" />
                    <div>
                      <p className="text-sm font-medium">Email</p>
                      <p className="text-sm text-muted-foreground">
                        {cotizacion.email}
                      </p>
                    </div>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleEmail}
                    className="ml-2"
                  >
                    <Mail className="mr-2 h-4 w-4" />
                    Enviar Email
                  </Button>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <Phone className="h-4 w-4 text-muted-foreground" />
                    <div>
                      <p className="text-sm font-medium">Teléfono</p>
                      <p className="text-sm text-muted-foreground">
                        {cotizacion.phone}
                      </p>
                    </div>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleWhatsApp}
                    className="ml-2 border-green-200 bg-green-50 text-green-700 hover:bg-green-100"
                  >
                    <MessageSquare className="mr-2 h-4 w-4" />
                    WhatsApp
                  </Button>
                </div>
              </div>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div className="flex items-center space-x-3">
                  <Building2 className="h-4 w-4 text-muted-foreground" />
                  <div>
                    <p className="text-sm font-medium">Empresa</p>
                    <p className="text-sm text-muted-foreground">
                      {cotizacion.company}
                    </p>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <MapPin className="h-4 w-4 text-muted-foreground" />
                  <div>
                    <p className="text-sm font-medium">Ubicación</p>
                    <p className="text-sm text-muted-foreground">
                      {cotizacion.location}
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Message */}
          <Card className={cardFade} style={{ animationDelay: "60ms" }}>
            <CardHeader>
              <CardTitle className="flex items-center">
                <MessageSquare className="mr-2 h-5 w-5" />
                Mensaje
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="rounded-md bg-muted/50 p-4">
                <p className="whitespace-pre-wrap">{cotizacion.message}</p>
              </div>
            </CardContent>
          </Card>

          {/* Cart Items */}
          {cartItems.length > 0 && (
            <Card className={cardFade} style={{ animationDelay: "120ms" }}>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Package className="mr-2 h-5 w-5" />
                  Productos Solicitados ({cartItems.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {cartItems.map((item, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between rounded-md bg-muted/50 p-3"
                    >
                      <div className="flex-1">
                        <p className="font-medium">{item.name}</p>
                        {item.description && (
                          <p className="text-sm text-muted-foreground">
                            {item.description}
                          </p>
                        )}
                      </div>
                      <div className="flex items-center space-x-4 text-sm">
                        <span className="font-medium">
                          Cantidad: {item.quantity}
                        </span>
                        {item.price ? (
                          <span className="font-medium text-green-600">
                            ${item.price.toLocaleString("es-CL")}
                          </span>
                        ) : null}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Timeline */}
          <Card className={cardFade} style={{ animationDelay: "180ms" }}>
            <CardHeader>
              <CardTitle className="flex items-center">
                <Clock className="mr-2 h-5 w-5" />
                Historial
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <div className="mt-2 h-2 w-2 rounded-full bg-blue-500"></div>
                  <div>
                    <p className="text-sm font-medium">
                      {cotizacion.is_quotation
                        ? "Solicitud de cotización recibida"
                        : "Consulta recibida"}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {formatDate(cotizacion.created_at)}
                    </p>
                  </div>
                </div>
                {cotizacion.updated_at !== cotizacion.created_at && (
                  <div className="flex items-start space-x-3">
                    <div className="mt-2 h-2 w-2 rounded-full bg-green-500"></div>
                    <div>
                      <p className="text-sm font-medium">
                        Última actualización
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {formatDate(cotizacion.updated_at)}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Type Info */}
          <Card className={cardFade} style={{ animationDelay: "90ms" }}>
            <CardHeader>
              <CardTitle>Tipo de Solicitud</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center space-x-2">
                {cotizacion.is_quotation ? (
                  <>
                    <ShoppingCart className="h-4 w-4 text-orange-600" />
                    <span className="font-medium text-orange-600">
                      Solicitud de Cotización
                    </span>
                  </>
                ) : (
                  <>
                    <MessageSquare className="h-4 w-4 text-blue-600" />
                    <span className="font-medium text-blue-600">
                      Consulta General
                    </span>
                  </>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Notes */}
          <Card className={cardFade} style={{ animationDelay: "150ms" }}>
            <CardHeader>
              <CardTitle>Notas Internas</CardTitle>
              <CardDescription>
                Agrega notas sobre el seguimiento y acciones tomadas
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <Textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Escribe notas sobre esta cotización..."
                rows={4}
              />
              <Button onClick={handleSave} disabled={saving} className="w-full">
                <Save className="mr-2 h-4 w-4" />
                {saving ? "Guardando..." : "Guardar Cambios"}
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default CotizacionDetailPage;
